import CrudPage from "components/CrudPage/CrudPage";
import { withRemoteDataAndSpinner } from "hoc/withRemoteDataAndSpinner";
import withRouter from "hoc/withRouter";
import { useLoadAll } from "hooks/useLoadAll";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import {
  MdArrowForward,
  MdCategory,
  MdEdit,
  MdHistory,
  MdNotes,
  MdPerson,
  MdPlace,
  MdPrint,
  MdQrCode2,
  MdScale,
  MdTrain,
} from "react-icons/md";
import { Link } from "react-router-dom";
import baggageService from "services/admin/baggageService";
import { baggageStatusColor, baggageStatusLabel } from "./baggage-status";

const formatDate = (d?: string) => (d ? new Date(d).toLocaleString("ru-RU") : "");

/** Заголовок секции внутри карточки — единый корпоративный стиль (как в мастере отправки). */
const SectionTitle = ({
  icon,
  children,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{ display: "flex", alignItems: "center", gap: 0.75, letterSpacing: 0.6, fontWeight: 700 }}
  >
    {icon}
    {children}
  </Typography>
);

/** Строка «иконка — подпись — значение» для сводки. */
const SummaryLine = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75 }}>
    <Box sx={{ color: "text.secondary", display: "flex", fontSize: 18 }}>{icon}</Box>
    <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, wordBreak: "break-word" }}>
      {value}
    </Typography>
  </Box>
);

interface BaggageDetailProps {
  router: any;
  data: any;
}

const BaggageDetail = ({ router, data }: BaggageDetailProps) => {
  const {
    params: { id },
  } = router;
  const baggage = data.baggage;

  const { data: tag, isLoading: isTagLoading } = useQuery({
    queryKey: ["baggage-tag", id],
    queryFn: async () => {
      const response: any = await baggageService.getTag(id);
      return response.data as { tagCode: string; qr: string };
    },
  });

  const handlePrint = async () => {
    const res: any = await baggageService.getTagPdf(id);
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
  };

  const trip = baggage?.trip;
  const customer = trip?.customer;
  const customerName =
    (customer &&
      (`${customer.lastName || ""} ${customer.firstName || ""}`.trim() || customer.name)) ||
    "—";

  // История перемещений — от новых к старым для удобства чтения ленты.
  const history: any[] = [...(baggage?.history || [])].sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
  );

  return (
    <CrudPage title={`Багаж · ${baggage?.tagCode || ""}`}>
      {/* Панель действий */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 3 }}>
        <Button variant="contained" startIcon={<MdPrint />} onClick={handlePrint}>
          Печать наклейку
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Левая колонка — сводка и история */}
        <Box
          sx={{
            flex: "2 1 420px",
            minWidth: 320,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Карточка сводки */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              {/* Шапка: код бирки + статус */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Код бирки
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                    {baggage?.tagCode || "—"}
                  </Typography>
                </Box>
                <Chip
                  color={baggageStatusColor(baggage?.status)}
                  label={baggageStatusLabel(baggage?.status)}
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Маршрут */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Откуда
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                    {trip?.originStation?.name || "—"}
                  </Typography>
                </Box>
                <Box sx={{ color: "primary.main", display: "flex", fontSize: 22 }}>
                  <MdArrowForward />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Куда
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                    {trip?.destinationStation?.name || "—"}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Детали */}
              <SummaryLine icon={<MdPerson />} label="Пассажир" value={customerName} />
              <SummaryLine icon={<MdTrain />} label="Поезд" value={trip?.train?.number || "—"} />
              <SummaryLine icon={<MdCategory />} label="Тип места" value={baggage?.type || "—"} />
              <SummaryLine
                icon={<MdScale />}
                label="Вес"
                value={baggage?.weight != null ? `${Number(baggage.weight)} кг` : "—"}
              />
              {baggage?.description ? (
                <SummaryLine icon={<MdNotes />} label="Описание" value={baggage.description} />
              ) : null}
            </CardContent>
          </Card>

          {/* Карточка истории */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <SectionTitle icon={<MdHistory />}>История перемещений</SectionTitle>

              {history.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Записей пока нет.
                </Typography>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {history.map((h: any, i: number) => {
                    const color = baggageStatusColor(h.status);
                    const dotColor = color === "default" ? "text.disabled" : `${color}.main`;
                    const isLast = i === history.length - 1;
                    return (
                      <Box key={h.id} sx={{ display: "flex", gap: 1.5 }}>
                        {/* Линия времени */}
                        <Box
                          sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: dotColor,
                              mt: 0.5,
                              flexShrink: 0,
                            }}
                          />
                          {!isLast && (
                            <Box sx={{ flex: 1, width: "2px", bgcolor: "divider", my: 0.5 }} />
                          )}
                        </Box>

                        {/* Содержимое записи */}
                        <Box sx={{ flex: 1, pb: isLast ? 0 : 2.5 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              size="small"
                              color={color}
                              variant="outlined"
                              label={baggageStatusLabel(h.status)}
                              sx={{ fontWeight: 600 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(h.createdAt)}
                            </Typography>
                          </Box>

                          {(h.station?.name || h.train?.number) && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {h.station?.name && (
                                <span
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                                >
                                  <MdPlace /> {h.station.name}
                                </span>
                              )}
                              {h.train?.number && (
                                <span
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                                >
                                  <MdTrain /> {h.train.number}
                                </span>
                              )}
                            </Typography>
                          )}

                          {h.note && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {h.note}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Правая колонка — бирка */}
        <Box sx={{ flex: "1 1 280px", minWidth: 260, maxWidth: 360 }}>
          <Card variant="outlined" sx={{ borderRadius: 2, position: "sticky", top: 16 }}>
            <CardContent sx={{ textAlign: "center" }}>
              <SectionTitle icon={<MdQrCode2 />}>Бирка</SectionTitle>

              {isTagLoading ? (
                <Box sx={{ py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {tag?.qr && (
                    <img
                      src={tag.qr}
                      alt="QR"
                      width={160}
                      height={160}
                      style={{ borderRadius: 8 }}
                    />
                  )}
                  {tag?.tagCode && (
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", letterSpacing: 1, color: "text.secondary" }}
                    >
                      {tag.tagCode}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </CrudPage>
  );
};

export default withRouter(
  withRemoteDataAndSpinner(BaggageDetail, (router: any) => {
    const requests: any = {
      baggage: () => baggageService.get(router.params.id),
    };
    return useLoadAll(requests);
  }),
);
